"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, Calendar, FileText, ShieldCheck, TrendingUp, UserCheck, ArrowRight, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function Localauthority() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: UserCheck,
    title: "100% Direct Access",
    desc: "You work directly with me, Yassine Essaadi — never passed to account managers."
  }, {
    icon: MapPin,
    title: "Telford & Shropshire Focus",
    desc: "Targeted Google Maps & organic search strategies engineered for local market domination."
  }, {
    icon: TrendingUp,
    title: "Sustainable Growth",
    desc: "White-hat technical & local SEO techniques that build long-term search authority."
  }, {
    icon: ShieldCheck,
    title: "Transparent Deliverables",
    desc: "Clear roadmaps, honest measurement, and zero jargon or hidden metrics."
  }];
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.5,
        delay: shouldReduceMotion ? 0 : custom * 0.1,
        ease: "easeOut"
      }
    })
  };
  return <section id="localauthorityhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 lg:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-20 size-96 rounded-full bg-accent/15 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <motion.div className="lg:col-span-7 flex flex-col gap-6" initial="hidden" animate="visible">
            <motion.div custom={0} variants={fadeUpVariant} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-transparent px-3 py-1 font-medium text-xs sm:text-sm tracking-wide rounded-md flex items-center gap-1.5">
                <MapPin className="size-3.5 text-accent" />
                <span>Telford &amp; Shropshire Local SEO Expert</span>
              </Badge>
              <Badge variant="outline" className="border-primary-foreground/20 text-primary-foreground/90 px-3 py-1 text-xs sm:text-sm rounded-md flex items-center gap-1">
                <Star className="size-3.5 fill-accent text-accent" />
                <span>Independent SEO Consultancy</span>
              </Badge>
            </motion.div>

            <motion.h1 custom={1} variants={fadeUpVariant} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">
              Local SEO Consultant in Telford
            </motion.h1>

            <motion.p custom={2} variants={fadeUpVariant} className="font-sans text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
              {"I'm Yassine Essaadi, an independent freelance SEO consultant helping businesses in Telford and Shropshire dominate local search results. When you hire me, you work directly with me — receiving tailored local SEO strategies, Google Business Profile optimisation, and transparent reporting to drive real, revenue-generating local leads."}
            </motion.p>

            <motion.div custom={3} variants={fadeUpVariant} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-medium text-base h-12 px-6 rounded-md">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground bg-primary/40 hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium text-base h-12 px-6 rounded-md">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <FileText className="size-5" />
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div custom={4} variants={fadeUpVariant} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-primary-foreground/15 mt-2">
              <div className="flex items-center gap-2 text-sm text-primary-foreground/90 font-medium">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Google Map Pack &amp; Local Organic Ranking</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/90 font-medium">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>No Lock-in Contracts or Agency Markups</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/90 font-medium">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Custom Keyword &amp; Competitor Audits</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/90 font-medium">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>Practical Implementation &amp; Honest Advice</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div className="lg:col-span-5 flex flex-col items-center" initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: shouldReduceMotion ? 0 : 0.6,
          delay: 0.2
        }}>
            <div className="relative w-full max-w-md bg-card text-card-foreground rounded-2xl p-6 shadow-xl border border-border/80">
              <div className="relative overflow-hidden rounded-xl bg-muted mb-6 aspect-4/3 sm:aspect-square flex items-center justify-center">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance Local SEO Consultant in Telford" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute bottom-3 left-3 right-3 bg-background/95 backdrop-blur-md p-3 rounded-lg border border-border text-center shadow-md">
                  <p className="font-serif font-bold text-foreground text-base sm:text-lg">
                    Yassine Essaadi
                  </p>
                  <p className="font-sans text-xs sm:text-sm text-muted-foreground font-medium">
                    Independent SEO Consultant &amp; Founder
                  </p>
                </div>
              </div>

              <div className="bg-muted/80 rounded-lg p-4 border border-border">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-primary text-primary-foreground shrink-0 mt-0.5">
                    <UserCheck className="size-5" />
                  </div>
                  <div>
                    <h2 className="font-serif font-semibold text-foreground text-sm">
                      Direct Consultant Guarantee
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {"You will communicate directly with me on every strategy session, review call, and deliverable — ensuring full accountability and personal dedication to your Telford business growth."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        <motion.div className="mt-16 pt-12 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" animate="visible">
          {trustHighlights.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} data-index={index} custom={5 + index} variants={fadeUpVariant} className="bg-primary-foreground/5 backdrop-blur-xs rounded-xl p-5 border border-primary-foreground/10 hover:border-accent/40 transition-[border-color,background-color] duration-300">
                <div className="size-10 rounded-lg bg-accent/20 text-accent flex items-center justify-center mb-3">
                  <Icon className="size-5" />
                </div>
                <h4 className="font-serif font-semibold text-primary-foreground text-base mb-1">
                  {item.title}
                </h4>
                <p className="font-sans text-xs text-primary-foreground/80 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>;
        })}
        </motion.div>

      </div>
    </section>;
}